import React, { useLayoutEffect, useRef } from 'react';
import cn from 'classnames';

import uniqueId from '../../utils/unique_id';

import './input.scss';

interface InputProps {
  onChange: (value: string | number) => void | undefined;
  value: string;
  defaultValue?: string;
  mask?: string;
  placeholder?: string;
  label?: string;
  type?: 'hidden' | 'text' | 'password';
  className?: string;
  maxLength?: number;
  height?: number;
  onEnter?: () => void | undefined;
  autoFocus?: boolean;
  counter?: boolean;
  isNumeric?: boolean;
  id?: string;
  autoComplete?: 'on' | 'off';
  postfixIcon?: string;
  onClickPostfixIcon?: () => void | undefined;
  onFocus?: () => any;
}

export const Input: React.FC<InputProps> = ({
  onChange,
  value,
  defaultValue,
  placeholder,
  label,
  type,
  className,
  maxLength,
  height,
  onEnter,
  autoFocus,
  counter,
  isNumeric,
  id = 'input' + uniqueId(),
  autoComplete,
  postfixIcon,
  onClickPostfixIcon,
  onFocus,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    if (autoFocus) {
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      });
    }
  }, [inputRef, autoFocus]);
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      onEnter && onEnter();
    }
  };
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    let _value = e.target.value;
    if (isNumeric) {
      _value = _value.replace(/\D/g, '');
    }
    if (_value !== value) {
      onChange(_value);
    }
  };
  const style = { height: height + 'px' };
  return (
    <div
      className={cn('input', className, {
        '--counter': counter,
        '--postfix': postfixIcon,
      })}
      style={style}>
      <input
        ref={inputRef}
        type={type}
        value={value || defaultValue}
        onChange={handleChange}
        placeholder={(!label && placeholder) || ''}
        maxLength={maxLength}
        onKeyDown={handleKeyDown}
        id={id}
        autoComplete={autoComplete}
        required={Boolean(!value && label)}
        onFocus={onFocus}
      />
      {label && (
        <label className="input__label" htmlFor={id}>
          {label}
        </label>
      )}
      {counter && (
        <div className="input__counter">
          {value.length}
          {maxLength !== -1 ? `/${maxLength}` : ''}
        </div>
      )}
      {!!postfixIcon && (
        <img src={postfixIcon} alt="" className="input__postfixicon" onClick={onClickPostfixIcon} />
      )}
    </div>
  );
};

Input.defaultProps = {
  type: 'text',
  value: '',
  defaultValue: '',
  placeholder: '',
  label: '',
  className: '',
  onChange: _ => undefined,
  onEnter: () => undefined,
  maxLength: -1,
  height: 48,
  autoFocus: false,
  counter: false,
  isNumeric: false,
  mask: '',
  autoComplete: 'off',
};
